import { useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Package, Users, Loader2 } from "lucide-react";
import { useGlobalSearch } from "../../hooks/useGlobalSearch";

const categoryIcons = {
  product: Package,
  customer: Users,
} as const;

const categoryLabels = {
  product: "Products",
  customer: "Customers",
} as const;

export function HeaderSearchBar() {
  const navigate = useNavigate();
  const { query, setQuery, results, isSearching, isOpen, close } =
    useGlobalSearch();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        close();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [close]);

  // Keyboard shortcut: Cmd/Ctrl+K to focus
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
      if (e.key === "Escape") {
        close();
        inputRef.current?.blur();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [close]);

  const handleSelect = (url: string) => {
    close();
    navigate(url);
  };

  // Group results by category
  const grouped = results.reduce(
    (acc, r) => {
      if (!acc[r.category]) acc[r.category] = [];
      acc[r.category].push(r);
      return acc;
    },
    {} as Record<string, typeof results>,
  );

  return (
    <div ref={containerRef} className="relative min-w-0 flex-1">
      <div className="group flex h-10 items-center rounded-xl border border-transparent bg-gray-50 px-3 transition-all focus-within:border-teal-200 focus-within:bg-white focus-within:ring-2 focus-within:ring-teal-100">
        {isSearching ? (
          <Loader2 className="mr-2.5 h-4 w-4 shrink-0 animate-spin text-teal-500" />
        ) : (
          <Search className="mr-2.5 h-4 w-4 shrink-0 text-gray-400 transition-colors group-focus-within:text-teal-500" />
        )}
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search medicines, patients…"
          aria-label="Search medicines and patients"
          className="min-w-0 w-full border-none bg-transparent text-sm text-gray-700 outline-none placeholder:text-gray-400"
        />
        {query && (
          <button
            type="button"
            onClick={close}
            aria-label="Clear search"
            className="ml-2 rounded-md px-1.5 py-1 text-[10px] font-medium text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            ESC
          </button>
        )}
        {!query && (
          <span className="ml-2 hidden shrink-0 rounded-md border border-gray-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-gray-400 sm:inline">
            ⌘K
          </span>
        )}
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-[100] max-h-[400px] overflow-y-auto">
          {isSearching && results.length === 0 ? (
            <div className="px-5 py-6 text-center text-sm text-gray-400">
              Searching...
            </div>
          ) : results.length === 0 && query.length >= 2 ? (
            <div className="px-5 py-6 text-center">
              <Search className="w-8 h-8 text-gray-200 mx-auto mb-2" />
              <p className="text-sm text-gray-400">
                No results for &ldquo;{query}&rdquo;
              </p>
            </div>
          ) : (
            Object.entries(grouped).map(([category, items]) => {
              const Icon =
                categoryIcons[category as keyof typeof categoryIcons] ||
                Package;
              const label =
                categoryLabels[category as keyof typeof categoryLabels] ||
                category;

              return (
                <div key={category}>
                  <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                    <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      <Icon className="w-3.5 h-3.5" />
                      {label}
                    </div>
                  </div>
                  {items.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleSelect(item.url)}
                      className="w-full px-5 py-3 flex items-center gap-3 hover:bg-teal-50 transition-colors text-left border-b border-gray-50 last:border-0"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {item.title}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {item.subtitle}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
